import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { logError } from "@/lib/logger";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CandidateDrawer from "@/components/CandidateDrawer";
import AddedByChip from "@/components/AddedByChip";
import SpocInlineModal from "@/components/SpocInlineModal";
import ReferenceScoreChip from "@/components/ReferenceScoreChip";
import VerifiedTierChip from "@/components/VerifiedTierChip";
import CvFreshnessBadge from "@/components/CvFreshnessBadge";
import { Plus, X, ArrowLeft, Sparkle, Share, Check, UploadSimple, FileCsv, Globe, UserPlus, Upload, User, Crown, Users as UsersIcon, EnvelopeSimple, Phone as PhoneIcon, Gift, CaretDown, CaretUp, FileText, PencilSimple, Bell, Clock, Warning, MapPin, Star, Link as LinkIcon, ChatCircleDots, Snowflake } from "@phosphor-icons/react";
import RescuePanel from "@/components/RescuePanel";
import KanbanCompactSettings from "@/components/KanbanCompactSettings";
import { smartPasteHandler, reformatPastedText } from "@/lib/smartPaste";
import { extractCandidateFieldsFromResume, mergeAutoFill } from "@/lib/resumeAutofill";
import AutocompleteInput from "@/components/AutocompleteInput";
import SkillsChipInput from "@/components/SkillsChipInput";
import ShareJobModal from "@/components/ShareJobModal";
import EditJobModal from "@/components/EditJobModal";
import AssignedRecruitersPanel from "@/components/AssignedRecruitersPanel";
import DuplicateCheckBanner from "@/components/DuplicateCheckBanner";
import { LOCATION_SUGGESTIONS } from "@/lib/suggestions";

const STAGES = ["Applied", "Screening", "Interview", "Offer", "Hired", "Rejected"];

const CURRENCY_SYMBOL = { INR: "₹", USD: "$", EUR: "€", GBP: "£" };
const currencySymbol = (code) => CURRENCY_SYMBOL[code] || `${code || ""} `;

const SOURCE_META = {
    self_apply: { label: "Self-apply", icon: User, cls: "text-cobalt" },
    public: { label: "Public link", icon: Globe, cls: "text-[#059669]" },
    manual: { label: "Tagged", icon: UserPlus, cls: "text-[#7E22CE]" },
    csv: { label: "CSV", icon: Upload, cls: "text-[#A3A3A3]" },
    daddytalk: { label: "DaddyTalk", icon: ChatCircleDots, cls: "text-cobalt" },
};

function SourceBadge({ source }) {
    const meta = SOURCE_META[source] || SOURCE_META.manual;
    const Icon = meta.icon;
    return (
        <span
            data-testid={`card-source-${source}`}
            className={`inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider ${meta.cls}`}
        >
            <Icon size={9} weight="bold" /> {meta.label}
        </span>
    );
}

// V2.0.52 (Feb 26, 2026) — a candidate counts as a "Direct apply" if
// they came through the public /apply/{id} form. Same test used by the
// "Direct Applies" filter chip + the green "Self-signup" origin pill
// on each Kanban card so behaviour stays consistent across the board.
function _isDirectApply(c) {
    if (!c) return false;
    const src = (c.source || "").toLowerCase();
    const origin = (c.origin || "").toLowerCase();
    return src === "public" || origin === "organic";
}



// V1.5.3 (a3) — compact kanban card avatar.
function _kanbanInitials(name) {
    return String(name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
}
function KanbanAvatar({ url, name }) {
    const [broken, setBroken] = React.useState(false);
    return (
        <div
            data-testid="kanban-avatar"
            className="shrink-0 w-7 h-7 bg-cobalt text-white text-[10px] font-black flex items-center justify-center overflow-hidden rounded-sm"
        >
            {url && !broken ? (
                <img src={url} alt="" onError={() => setBroken(true)}
                     className="w-full h-full object-cover" />
            ) : (
                <span>{_kanbanInitials(name)}</span>
            )}
        </div>
    );
}

function ScoreBadge({ score }) {
    if (score == null)
        return (
            <span className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
                Not scored
            </span>
        );
    let cls = "bg-[#FEF2F2] text-[#DC2626]";
    if (score >= 75) cls = "bg-[#ECFDF5] text-[#059669]";
    else if (score >= 55) cls = "bg-[#EFF6FF] text-[#F59E0B]";
    else if (score >= 35) cls = "bg-[#F5F5F5] text-[#525252]";
    return (
        <span className={`px-2 py-0.5 text-[10px] font-black uppercase tracking-wider ${cls} inline-flex items-center gap-1`}>
            <Sparkle size={10} weight="fill" /> {score}%
        </span>
    );
}

/**
 * KanbanCompactBadges — V2.1.1 iter169 (Feb 2026).
 *
 * Replaces the CSS-hidden compact hack. Renders **at most 3 badges** per
 * card, chosen from a priority order so a recruiter sees the ONE thing
 * that matters for each candidate in a dense board.
 *
 * Priority (highest first — first match wins):
 *   1. Action needed  — Outcome pending on Offer/Hired, Email delayed,
 *                       Awaiting reg (>=3d).
 *   2. Result signal  — Selected / Accepted / Joined / Rejected /
 *                       No-show / Rescheduled.
 *   3. Context        — Boosted, Self-signup, Fast-Track, Referral,
 *                       Sourced (default).
 *
 * We always show ORIGIN so the recruiter knows *how* the person landed.
 * Score is rendered separately on the right of the card so it isn't
 * counted here.
 */
/**
 * Compact-badge catalogue.
 *
 * Each entry is a self-contained { key, when(c), render() } row. The
 * compact card picks the first 3 (by user's saved priority order) that
 * pass `when(c)`. This replaces the earlier if/else stack so a recruiter
 * can drag-reorder priority and have it stick per-device.
 *
 * V2.1.1 iter170 — user-configurable order lives in
 * `localStorage.kanban_compact_priority_v1` as a JSON array of keys.
 * Missing keys fall through to `DEFAULT_COMPACT_ORDER`.
 */
const COMPACT_CATALOGUE = {
    "outcome-pending": {
        label: "Outcome pending",
        cls: "bg-[#FEF3C7] text-[#92400E]",
        when: (c) => c.stage === "Offer" && !c.offer_outcome,
    },
    "confirm-joining": {
        label: "Confirm joining",
        cls: "bg-[#FEF3C7] text-[#92400E]",
        when: (c) => c.stage === "Hired" && !c.hire_outcome,
    },
    "email-delayed": {
        label: "Email delayed",
        cls: "bg-orange-100 text-orange-900",
        when: (c) => !!c.email_delayed,
    },
    "awaiting-reg": {
        label: (c) => `Awaiting reg · ${c.awaiting_reg_days}d`,
        cls: "bg-amber-100 text-amber-900",
        when: (c) => !!c.is_awaiting_reg && (c.awaiting_reg_days || 0) >= 3,
    },
    "no-show": {
        label: "No-show",
        cls: "bg-red-100 text-red-800",
        when: (c) => c.hire_outcome?.status === "no_show",
    },
    "joined": {
        label: "Joined",
        cls: "bg-emerald-100 text-emerald-800",
        when: (c) => c.hire_outcome?.status === "hired",
    },
    "offer-accepted": {
        label: "Offer accepted",
        cls: "bg-emerald-100 text-emerald-800",
        when: (c) => c.offer_outcome?.status === "accepted",
    },
    "offer-declined": {
        label: "Offer declined",
        cls: "bg-red-100 text-red-800",
        when: (c) => c.offer_outcome?.status === "rejected",
    },
    "selected": {
        label: "Selected",
        cls: "bg-emerald-100 text-emerald-800",
        when: (c) => c.interview_outcome?.status === "selected",
    },
    "rescheduled": {
        label: "↻ Rescheduled",
        cls: "bg-amber-100 text-amber-800",
        when: (c) => c.interview_outcome?.status === "rescheduled",
    },
    "boosted": {
        label: "★ Boosted",
        cls: "bg-[#F59E0B] text-white",
        when: (c) => !!c.is_boosted,
    },
    "self-signup": {
        label: "Self-signup",
        cls: "bg-emerald-100 text-emerald-800",
        when: (c, ctx) => !!ctx.isDirectApply,
    },
    "fast-track": {
        label: "Fast-Track",
        cls: "bg-cobalt/10 text-cobalt",
        when: (c) => c.origin === "daddytalk_fast_track" || (c.source || "").toLowerCase() === "fast_track",
    },
    "referral": {
        label: "Referral",
        cls: "bg-emerald-100 text-emerald-800",
        when: (c) => (c.source || "").toLowerCase() === "referral",
    },
    "sourced": {
        label: "Sourced",
        cls: "bg-amber-100 text-amber-800",
        when: (c, ctx) =>
            // fallback origin — only fires if none of the specific origin
            // chips above matched.
            !ctx.isDirectApply
            && c.origin !== "daddytalk_fast_track"
            && (c.source || "").toLowerCase() !== "fast_track"
            && (c.source || "").toLowerCase() !== "referral",
    },
};

// Priority order (highest urgency first). User can override via
// KanbanCompactSettings; missing keys are appended at the end.
export const DEFAULT_COMPACT_ORDER = [
    "outcome-pending", "confirm-joining", "email-delayed", "awaiting-reg",
    "no-show", "joined", "offer-accepted", "offer-declined",
    "selected", "rescheduled",
    "boosted",
    "self-signup", "fast-track", "referral", "sourced",
];

export const COMPACT_STORAGE_KEY = "kanban_compact_priority_v1";

export function loadCompactOrder() {
    try {
        const raw = localStorage.getItem(COMPACT_STORAGE_KEY);
        if (!raw) return DEFAULT_COMPACT_ORDER;
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed) || parsed.length === 0) return DEFAULT_COMPACT_ORDER;
        // Merge: user's ordering first, then any new keys we added later.
        const set = new Set(parsed);
        return [...parsed.filter((k) => k in COMPACT_CATALOGUE),
                ...DEFAULT_COMPACT_ORDER.filter((k) => !set.has(k))];
    } catch { return DEFAULT_COMPACT_ORDER; }
}

export function saveCompactOrder(order) {
    try { localStorage.setItem(COMPACT_STORAGE_KEY, JSON.stringify(order)); } catch { /* ignore */ }
}

function KanbanCompactBadges({ c, isDirectApply, order }) {
    const priority = order || DEFAULT_COMPACT_ORDER;
    const ctx = { isDirectApply };
    const applicable = [];
    for (const key of priority) {
        const spec = COMPACT_CATALOGUE[key];
        if (!spec) continue;
        if (spec.when(c, ctx)) {
            applicable.push({
                k: key,
                cls: spec.cls,
                label: typeof spec.label === "function" ? spec.label(c) : spec.label,
            });
            if (applicable.length >= 3) break;
        }
    }
    return (
        <div
            className="flex flex-wrap gap-1"
            data-testid={`kanban-compact-badges-${c.id}`}
        >
            {applicable.map((b) => (
                <span
                    key={b.k}
                    data-testid={`kanban-compact-${b.k}-${c.id}`}
                    className={`inline-flex items-center gap-0.5 text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 ${b.cls}`}
                >
                    {b.label}
                </span>
            ))}
        </div>
    );
}

export const COMPACT_BADGE_LABELS = Object.fromEntries(
    Object.entries(COMPACT_CATALOGUE).map(([k, v]) => [
        k, typeof v.label === "function" ? v.label({awaiting_reg_days: 3}) : v.label
    ])
);

/** Composite hiring-conviction badge — visible only when the recruiter
 * has toggled the Kanban into 'conviction' sort mode AND the candidate
 * has BOTH signals (a match score and at least one submitted reference).
 * The formula (0.6·match + 0.4·refAvg·20) is computed in `convictionOf`
 * and passed in as `score` so the same rank is reflected on-card.
 */
function ConvictionBadge({ score, testid }) {
    if (score == null) return null;
    const rounded = Math.round(score);
    let cls = "bg-ink text-white";
    if (rounded >= 75) cls = "bg-emerald-900 text-white";
    else if (rounded >= 55) cls = "bg-amber-700 text-white";
    else if (rounded >= 35) cls = "bg-[#525252] text-white";
    else cls = "bg-rose-700 text-white";
    return (
        <span
            data-testid={testid || "conviction-badge"}
            title={`Hiring conviction · ${rounded}/100 (60% AI match + 40% reference rating)`}
            className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black tracking-wider ${cls}`}
        >
            ✨ {rounded}
            <span className="text-[8px] opacity-80">CONV</span>
        </span>
    );
}

const emptyCand = {
    name: "",
    email: "",
    phone: "",
    current_title: "",
    location: "",
    years_experience: 0,
    resume_text: "",
    linkedin: "",
    stage: "Applied",
};

export default function JobDetail() {
    const { id } = useParams();
    const { user: me } = useAuth();
    const canWrite = ["admin", "recruiter", "hiring_manager", "superadmin"].includes(me?.role);
    // P0 RBAC — Recruiters can no longer edit requirements. Add/remove
    // candidates, drag on the Kanban and stage moves still allowed via
    // canWrite.
    const canEditJob = ["admin", "hiring_manager", "superadmin"].includes(me?.role);
    const [job, setJob] = useState(null);
    const [cands, setCands] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [rescueOpen, setRescueOpen] = useState(false);
    // V2.1.1 iter170 — user-configurable compact-mode badge priority
    const [compactOrder, setCompactOrder] = useState(() => loadCompactOrder());
    const [compactSettingsOpen, setCompactSettingsOpen] = useState(false);
    const [form, setForm] = useState(emptyCand);
    const [candSkills, setCandSkills] = useState([]);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [dragId, setDragId] = useState(null);
    const [copied, setCopied] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [csvImporting, setCsvImporting] = useState(false);
    const [csvResult, setCsvResult] = useState(null);
    const [jdExpanded, setJdExpanded] = useState(false);
    const [officeExpanded, setOfficeExpanded] = useState(true);
    // V1.5.5.13 — inline "Add SPOC" modal state (empty-state CTA on the
    // Interview office card).
    const [showAddSpocModal, setShowAddSpocModal] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [dupStatus, setDupStatus] = useState(null); // null | 'warn' | 'block'
    const [nudgingId, setNudgingId] = useState(null); // cid currently being nudged (kanban Nudge button)

    // V1.4 — "Awaiting reg for X days" filter. Threshold comes from workspace
    // settings so each team can tune it (aggressive teams flag at 1 day, ops-
    // heavy teams stay at 7). Falls back to 3 while settings load.
    const [awaitingRegOnly, setAwaitingRegOnly] = useState(false);
    const [awaitingRegThreshold, setAwaitingRegThreshold] = useState(3);
    useEffect(() => {
        api.get("/workspace/settings")
            .then((r) => {
                const t = Number(r.data?.awaiting_reg_threshold_days);
                if (Number.isFinite(t) && t > 0) setAwaitingRegThreshold(t);
            })
            .catch(() => { /* keep default */ });
    }, []);

    const nudgeCandidate = async (cid, e) => {
        e?.stopPropagation();
        e?.preventDefault();
        if (nudgingId) return;
        setNudgingId(cid);
        try {
            // V1.5.8.16 — was hitting legacy /candidates/:cid/drip/nudge (404 in
            // prod after the auth-helpers extract). Aligned with PersonDetailPanel
            // to use /nudge-registration which properly counts against the max-5
            // quota and returns drip_state.sent_count for the toast.
            const { data } = await api.post(`/candidates/${cid}/nudge-registration`);
            const n = data?.drip_state?.sent_count ?? 0;
            toast.success(`Reminder sent (${n}/5).`);
            // refresh drip counter on the card
            load();
        } catch (ex) {
            toast.error(formatApiError(ex) || "Nudge failed.");
        } finally {
            setNudgingId(null);
        }
    };

    const load = useCallback(async () => {
        try {
            const [j, c] = await Promise.all([
                api.get(`/jobs/${id}`),
                api.get(`/candidates`, { params: { job_id: id } }),
            ]);
            setJob(j.data);
            setCands(c.data);
        } catch (e) {
            logError("Failed to load job/candidates:", e);
        }
    }, [id]);
    useEffect(() => {
        load();
    }, [load]);

    // Keep the Add-Candidate form state clean when it's hidden.
    useEffect(() => {
        if (!showForm) {
            setCandSkills([]);
        }
    }, [showForm]);

    // Feb 2026 (V1.3) — Sort mode toggle:
    //   'match'      → order by AI-match score only (backwards compatible).
    //   'conviction' → composite = 0.6·match + 0.4·(ref_avg × 20).
    //   'recent'     → V2.0.52 — most-recently added/updated first. Best
    //                  for teams that want "who applied today" at the top
    //                  of every column. Now the default (per user ask
    //                  Feb 26, 2026: "new candidate and modify candidate
    //                  should always sort in most recent").
    // Falls back to raw match_score when the candidate has no reference
    // signal so unreferenced candidates aren't unfairly penalised.
    // Feb 11 update — persisted per-job so recruiters can prefer AI-match
    // on some roles (e.g. hard-skill filters) and Conviction on others
    // (e.g. senior/high-touch hires) without one setting clobbering the other.
    const _sortModeKey = `kanban_sort_mode:${id}`;
    const [sortMode, setSortMode] = useState(() => {
        try {
            // Preserve legacy global choice on first read for existing users.
            return (
                localStorage.getItem(_sortModeKey)
                || localStorage.getItem("kanban_sort_mode")
                || "recent"
            );
        } catch { return "recent"; }
    });
    useEffect(() => {
        try {
            localStorage.setItem(_sortModeKey, sortMode);
        } catch {
            // localStorage disabled (private mode / SSR) — sort mode still works in memory.
        }
    }, [sortMode, _sortModeKey]);

    // V2.1.0 (Feb 2026, Phase 1) — Kanban density toggle. Compact hides
    // secondary badges via CSS so recruiters can scan a busy pipeline
    // without noise. Persisted per-user; global (not per-job) so the
    // preference travels across all Kanban boards.
    const [kanbanDensity, setKanbanDensity] = useState(() => {
        try { return localStorage.getItem("kanban_density") || "cozy"; } catch { return "cozy"; }
    });
    useEffect(() => {
        try { localStorage.setItem("kanban_density", kanbanDensity); } catch { /* ignore */ }
    }, [kanbanDensity]);

    // V2.0.52 (Feb 26, 2026) — "Direct Applies" filter. When on, hides
    // recruiter-added / DaddyTalk / referral candidates and shows ONLY
    // rows that came through the public /apply/{id} form. Users asked
    // for a way to quickly triage inbound public-apply traffic.
    const _directFilterKey = `kanban_direct_only:${id}`;
    const [directOnly, setDirectOnly] = useState(() => {
        try { return localStorage.getItem(_directFilterKey) === "1"; }
        catch { return false; }
    });
    useEffect(() => {
        try {
            if (directOnly) localStorage.setItem(_directFilterKey, "1");
            else localStorage.removeItem(_directFilterKey);
        } catch { /* private mode */ }
    }, [directOnly, _directFilterKey]);

    const convictionOf = useCallback((c) => {
        const match = Number.isFinite(c.match_score) ? c.match_score : 0;
        const refAvg = c.reference_summary?.avg_rating;
        if (!refAvg) return match;
        return 0.6 * match + 0.4 * (refAvg * 20);
    }, []);

    const grouped = STAGES.reduce((acc, s) => {
        // Sort each column: boosted candidates first, then by chosen mode desc.
        // Keeps recruiters focused on paying candidates first inside every stage.
        acc[s] = cands
            .filter((c) => c.stage === s)
            .filter((c) => !awaitingRegOnly || c.is_awaiting_reg)
            // V2.0.52 — Direct Applies filter.
            .filter((c) => !directOnly || _isDirectApply(c))
            .sort((a, b) => {
                if (!!b.is_boosted !== !!a.is_boosted) return b.is_boosted ? 1 : -1;
                if (sortMode === "conviction") {
                    return convictionOf(b) - convictionOf(a);
                }
                if (sortMode === "recent") {
                    // V2.0.79 — recruiters expect "recent" = recently
                    // *added*. `updated_at` gets bumped by background
                    // processes (signals refresh, priority recompute,
                    // drip state), so old candidates would jump to top
                    // after any edit. Use `created_at` and only fall
                    // back to `updated_at` when created_at is missing.
                    const at = a.created_at || a.updated_at || "";
                    const bt = b.created_at || b.updated_at || "";
                    if (at === bt) return 0;
                    return bt > at ? 1 : -1;
                }
                return (b.match_score ?? -1) - (a.match_score ?? -1);
            });
        return acc;
    }, {});
    const boostedCount = cands.filter((c) => c.is_boosted).length;
    const awaitingRegCount = cands.filter((c) => c.is_awaiting_reg).length;
    // V2.0.52 — count for the "Direct applies" chip badge.
    const directApplyCount = cands.filter(_isDirectApply).length;

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setSaving(true);
        try {
            const payload = {
                ...form,
                years_experience: Number(form.years_experience) || 0,
                skills: candSkills,
                job_id: id,
            };
            await api.post("/candidates", payload);
            setForm(emptyCand);
            setCandSkills([]);
            setShowForm(false);
            load();
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    const onDrop = async (stage) => {
        if (!dragId) return;
        const cid = dragId;
        const dragged = cands.find((c) => c.id === cid);
        setDragId(null);
        // Feb 2026 — Mandatory-registration gate. Warn before hitting 409.
        if (
            dragged &&
            !dragged.candidate_user_id &&
            dragged.drip_state?.status === "active" &&
            !["Applied", "Rejected"].includes(stage)
        ) {
            toast.info(
                `${dragged.name || "Candidate"} hasn't registered yet — send a reminder before moving to ${stage}.`,
                { duration: 5000 },
            );
            return;
        }
        setCands((prev) => prev.map((c) => (c.id === cid ? { ...c, stage } : c)));
        try {
            await api.patch(`/candidates/${cid}/stage`, { stage });
        } catch (ex) {
            // Feb 18, 2026 — HTTPException detail may be an object
            // (intake-incomplete gate). Stringify safely to avoid the
            // React error #31 blank-page crash.
            const detail = ex?.response?.data?.detail;
            if (detail && typeof detail === "object" && detail.code === "INTAKE_INCOMPLETE") {
                const missing = Array.isArray(detail.missing) ? detail.missing : [];
                toast.error(
                    detail.message || "Intake form is not fully filled.",
                    missing.length ? { description: `Missing: ${missing.slice(0, 5).join(", ")}${missing.length > 5 ? "…" : ""}` } : undefined,
                );
            } else {
                toast.error(formatApiError(detail) || "Couldn't update stage.");
            }
            load();
        }
    };

    const publicUrl = `${window.location.origin}/api/share/j/${id}`;
    const copyPublicLink = async () => {
        // Try modern clipboard API (works on desktop + non-iframe mobile).
        let ok = false;
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(publicUrl);
                ok = true;
            }
        } catch (e) {
            logError("Clipboard write failed:", e);
        }
        // Fallback: legacy execCommand (works in iframes without clipboard-write permission).
        if (!ok) {
            try {
                const ta = document.createElement("textarea");
                ta.value = publicUrl;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.focus();
                ta.select();
                ok = document.execCommand("copy");
                document.body.removeChild(ta);
            } catch (e) {
                logError("execCommand copy failed:", e);
            }
        }
        // Always open the share modal so the user sees the URL even if copy failed.
        setShareOpen(true);
        if (ok) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const nativeShare = null;  // Removed — replaced by ShareJobModal rich share sheet
    void nativeShare;

    const handlePdf = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setErr("");
        setPdfLoading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/resumes/parse", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const parsedText = reformatPastedText(r.data.text);
            // Auto-populate empty form fields (name/email/phone/location/title/linkedin/years/skills)
            // from the top of the parsed text. Never overwrites recruiter-typed values.
            const extracted = extractCandidateFieldsFromResume(parsedText);
            // Peel skills out so we can push them into the SkillsChipInput state.
            const { skills: extractedSkills, ...formExtracted } = extracted;
            setForm((f) => {
                const merged = mergeAutoFill({ ...f, resume_text: parsedText }, formExtracted);
                if (merged.filledCount > 0) {
                    const fields = Object.keys(formExtracted)
                        .filter((k) => formExtracted[k] && !f[k])
                        .join(", ");
                    const skillsAdded = extractedSkills?.length > 0 && candSkills.length === 0
                        ? `, ${extractedSkills.length} skills` : "";
                    toast.success(
                        `Auto-filled ${merged.filledCount} field${merged.filledCount > 1 ? "s" : ""}${skillsAdded}: ${fields}`,
                        { duration: 4000 }
                    );
                }
                return merged.form;
            });
            // Fill skills state if currently empty
            if (extractedSkills?.length > 0 && candSkills.length === 0) {
                setCandSkills(extractedSkills);
            }
        } catch (ex) {
            setErr(formatApiError(ex.response?.data?.detail) || ex.message);
        } finally {
            setPdfLoading(false);
            e.target.value = "";
        }
    };

    const handleCsvImport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setCsvImporting(true);
        setCsvResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post(`/jobs/${id}/import`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setCsvResult(r.data);
            load();
        } catch (ex) {
            setCsvResult({
                error: formatApiError(ex.response?.data?.detail) || ex.message,
            });
        } finally {
            setCsvImporting(false);
            e.target.value = "";
        }
    };

    if (!job) return <div className="p-10">Loading…</div>;

    return (
        <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-[1600px] w-full min-w-0 mx-auto" data-testid="job-detail-page" style={{ maxWidth: "min(1600px, 100vw)" }}>
            <Link
                to="/requirements"
                className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] hover:text-ink flex items-center gap-1 mb-4"
            >
                <ArrowLeft size={12} weight="bold" /> All requirements
            </Link>

            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-2">
                <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2 flex items-center gap-3 flex-wrap">
                        {job.code && (
                            <span
                                data-testid="job-code"
                                title="Requirement code — unique per workspace"
                                className="bg-ink text-white px-2 py-0.5 font-mono text-[10px] tracking-wider"
                            >
                                {job.code}
                            </span>
                        )}
                        <span>{job.department || "General"} · {job.location || "Remote"}</span>
                    </p>
                    <h1
                        className="font-heading text-3xl sm:text-5xl font-black tracking-tighter text-ink break-words"
                        data-testid="job-title-heading"
                    >
                        {job.title}
                    </h1>
                </div>
                <div className="flex flex-wrap gap-2 md:self-auto shrink-0">
                    <button
                        onClick={copyPublicLink}
                        data-testid="copy-public-link-button"
                        className="border border-[#E5E5E5] hover:border-ink text-ink px-4 sm:px-5 py-2.5 sm:py-3 font-bold tracking-wide text-sm flex items-center gap-2 transition-colors"
                    >
                        {copied ? <Check size={14} weight="bold" /> : <Share size={14} weight="bold" />}
                        {copied ? "Link copied" : "Share public link"}
                    </button>
                    {canWrite && (
                        <label
                            data-testid="csv-import-label"
                            className="border border-[#E5E5E5] hover:border-ink text-ink px-4 sm:px-5 py-2.5 sm:py-3 font-bold tracking-wide text-sm flex items-center gap-2 cursor-pointer transition-colors"
                        >
                            <FileCsv size={14} weight="bold" />
                            {csvImporting ? "Importing…" : "Import CSV"}
                            <input
                                data-testid="csv-import-input"
                                type="file"
                                accept=".csv,text/csv"
                                className="hidden"
                                onChange={handleCsvImport}
                                disabled={csvImporting}
                            />
                        </label>
                    )}
                    {canEditJob && (
                        <button
                            onClick={() => setEditOpen(true)}
                            data-testid="edit-job-button"
                            className="border border-[#E5E5E5] hover:border-cobalt hover:text-cobalt text-ink px-4 sm:px-5 py-2.5 sm:py-3 font-bold tracking-wide text-sm flex items-center gap-2 transition-colors"
                        >
                            <PencilSimple size={14} weight="bold" /> Edit
                        </button>
                    )}
                    {canWrite && (
                        <button
                            onClick={() => setRescueOpen(true)}
                            data-testid="open-rescue-button"
                            title="Resurface cold candidates who match this role"
                            className="border-2 border-ink hover:bg-ink hover:text-white text-ink px-4 sm:px-5 py-2.5 sm:py-3 font-bold tracking-wide text-sm flex items-center gap-2 transition-colors"
                        >
                            <Snowflake size={14} weight="bold" /> Rescue
                        </button>
                    )}
                    {canWrite && (
                        <button
                            onClick={() => setShowForm(true)}
                            data-testid="open-new-candidate-button"
                            className="bg-cobalt hover:bg-cobalt-dark text-white px-5 py-2.5 sm:px-6 sm:py-3 font-bold tracking-wide text-sm flex items-center gap-2 transition-colors"
                        >
                            <Plus size={14} weight="bold" /> Add candidate
                        </button>
                    )}
                </div>
            </div>
            {csvResult && (
                <div
                    data-testid="csv-result"
                    className={`mb-4 border px-4 py-3 text-sm ${
                        csvResult.error
                            ? "border-[#DC2626] bg-[#FEF2F2] text-[#DC2626]"
                            : "border-[#059669] bg-[#ECFDF5] text-[#059669]"
                    }`}
                >
                    {csvResult.error ? (
                        <span>CSV import failed: {csvResult.error}</span>
                    ) : (
                        <span>
                            Imported <strong>{csvResult.created}</strong> candidates
                            {csvResult.errors?.length > 0 &&
                                ` · ${csvResult.errors.length} row(s) skipped`}
                            . Hint: CSV headers should be{" "}
                            <code className="font-mono">name, email, current_title, location, years_experience, skills, resume_text, linkedin, phone</code>.
                        </span>
                    )}
                </div>
            )}
            <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 bg-[#ECFDF5] text-[#059669]">
                    {job.status}
                </span>
                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 bg-[#F5F5F5] text-[#525252]">
                    {job.employment_type}
                </span>
                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 bg-[#F5F5F5] text-[#525252]">
                    {job.seniority}
                </span>
                {job.salary_range && (
                    <span className="text-[10px] font-black uppercase tracking-wider px-2 py-1 bg-[#F5F5F5] text-[#525252]">
                        {typeof job.salary_range === "string"
                            ? job.salary_range
                            : [job.salary_range.min, job.salary_range.max]
                                  .filter((v) => v != null)
                                  .join(" – ")
                              + (job.salary_range.currency ? ` ${job.salary_range.currency}` : "")}
                    </span>
                )}
                {job.referral_eligible && job.referral_bounty_amount != null && (
                    <span
                        data-testid="job-referral-bounty-chip"
                        title="Referral bounty for this role"
                        className="text-[10px] font-black uppercase tracking-wider px-2 py-1 bg-gradient-to-r from-[#FFFBEB] to-[#FEF3C7] text-[#92400E] border border-[#F59E0B]/50 inline-flex items-center gap-1"
                    >
                        <Gift size={10} weight="fill" /> Refer &amp; earn{" "}
                        {currencySymbol(job.referral_bounty_currency)}
                        {Number(job.referral_bounty_amount).toLocaleString("en-IN")}
                    </span>
                )}
            </div>

            {/* Batch 3 (Feb 2026) — client-less requirement warning. */}
            {!job.client_id && (
                <div
                    data-testid="job-no-client-banner"
                    className="mb-6 bg-[#FFFBEB] border border-[#FDE68A] p-4 flex items-start gap-3"
                >
                    <div className="text-[#B45309] shrink-0 mt-0.5">⚠️</div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-[#B45309]">
                            No client attached to this requirement
                        </p>
                        <p className="text-xs text-[#78350F] mt-1">
                            Testimonial requests, white-label career pages, and
                            recruiter share links won&apos;t work until you attach a client.
                            Click <span className="font-bold">Edit</span> above → set the
                            Client field → Save.
                        </p>
                    </div>
                </div>
            )}

            {/* Batch 2 — assigned recruiters (admin/HM assign, recruiter read-only) */}
            <AssignedRecruitersPanel
                jobId={job.id}
                assignedRecruiters={job.assigned_recruiters || []}
                onChange={(updated) => setJob(updated)}
            />

            {/* V1.5.5 Phase 2 — Client office + primary SPOC (collapsible card) */}
            {job.primary_location && (
                <div
                    data-testid="job-primary-location-card"
                    className="mb-8 bg-white border border-[#E5E5E5]"
                >
                    <button
                        type="button"
                        data-testid="job-office-toggle"
                        onClick={() => setOfficeExpanded((v) => !v)}
                        className="w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-3 hover:bg-[#FAFAFA] transition-colors"
                        aria-expanded={officeExpanded}
                    >
                        <div className="flex items-center gap-2 min-w-0">
                            <div className="w-7 h-7 bg-cobalt text-white flex items-center justify-center shrink-0">
                                <MapPin size={13} weight="fill" />
                            </div>
                            <div className="text-left min-w-0">
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3]">
                                    Interview office
                                </p>
                                <p className="font-bold text-ink text-sm truncate">
                                    {job.primary_location.label}
                                    {job.primary_location.city && job.primary_location.city.toLowerCase() !== (job.primary_location.label || "").toLowerCase()
                                        ? ` · ${job.primary_location.city}` : ""}
                                    {" · "}
                                    <span className="text-[#525252] font-normal">
                                        {(job.primary_location.spocs || []).length}{" "}
                                        SPOC{(job.primary_location.spocs || []).length === 1 ? "" : "s"}
                                    </span>
                                </p>
                            </div>
                        </div>
                        {officeExpanded ? (
                            <CaretUp size={16} weight="bold" className="text-[#525252] shrink-0" />
                        ) : (
                            <CaretDown size={16} weight="bold" className="text-[#525252] shrink-0" />
                        )}
                    </button>

                    {officeExpanded && (
                        <div
                            data-testid="job-office-body"
                            className="border-t border-[#E5E5E5] px-4 sm:px-5 py-4"
                        >
                    <div className="flex items-start gap-3">
                        <MapPin size={18} weight="fill" className="text-cobalt mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <p className="text-base font-bold text-ink flex items-center gap-2">
                                {job.primary_location.label}
                                {job.primary_location.is_primary && (
                                    <span className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-100 px-1.5 py-0.5">
                                        <Star size={9} weight="fill" /> Primary
                                    </span>
                                )}
                            </p>
                            <p className="text-sm text-[#525252] mt-0.5">
                                {job.primary_location.address_line_1}
                                {job.primary_location.address_line_2 ? `, ${job.primary_location.address_line_2}` : ""}
                                {job.primary_location.city ? `, ${job.primary_location.city}` : ""}
                                {job.primary_location.state ? `, ${job.primary_location.state}` : ""}
                                {job.primary_location.pincode ? ` ${job.primary_location.pincode}` : ""}
                            </p>
                            {job.primary_location.google_maps_url && (
                                <a
                                    href={job.primary_location.google_maps_url}
                                    target="_blank" rel="noopener noreferrer"
                                    data-testid="job-primary-location-maps"
                                    className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-cobalt hover:underline"
                                >
                                    <LinkIcon size={11} weight="bold" /> Open in Google Maps
                                </a>
                            )}
                        </div>
                    </div>
                    {/* V1.5.5.5 — Always render the SPOC section so recruiters
                        know who owns the requirement. Three states:
                        - Explicit `primary_spoc_id` picked on the job → highlight it + list peers.
                        - No explicit pick but the office has SPOCs → fall back to the location's primary SPOC (server hydrates).
                        - Office has zero SPOCs → clear CTA to add one from the Client page. */}
                    <div
                        data-testid="job-office-spocs-section"
                        className="mt-4 pt-4 border-t border-[#F5F5F5]"
                    >
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] inline-flex items-center gap-1.5">
                                <UsersIcon size={11} weight="bold" /> Office SPOCs
                            </p>
                            {job.client_id && (
                                <Link
                                    to={`/clients?edit=${job.client_id}#locations`}
                                    data-testid="job-manage-spocs-link"
                                    className="text-[10px] font-bold text-cobalt hover:underline uppercase tracking-wider"
                                >
                                    Manage on client →
                                </Link>
                            )}
                        </div>

                        {(job.primary_location?.spocs || []).length === 0 ? (
                            <div
                                data-testid="job-no-spocs-empty-state"
                                className="text-[11px] leading-relaxed text-[#B45309] bg-[#FFFBEB] border border-[#FDE68A] p-3"
                            >
                                <p className="mb-2">
                                    No SPOC configured for this office yet. Recruiters
                                    won&apos;t know who to route candidates to.
                                </p>
                                {job.client_id && (
                                    <div className="flex flex-wrap items-center gap-3 mt-2">
                                        <button
                                            type="button"
                                            data-testid="job-inline-add-spoc-btn"
                                            onClick={() => setShowAddSpocModal(true)}
                                            className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider bg-cobalt text-white px-3 py-1.5 hover:bg-cobalt-dark"
                                        >
                                            <Plus size={11} weight="bold" /> Add SPOC now
                                        </button>
                                        <Link
                                            to={`/clients?edit=${job.client_id}#locations`}
                                            className="text-[10px] font-bold uppercase tracking-wider text-[#B45309] hover:underline"
                                        >
                                            Or manage on client page →
                                        </Link>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                {(job.primary_location.spocs || []).map((s) => {
                                    const isPicked = s.id === job.primary_spoc_id ||
                                        (!job.primary_spoc_id && s.id === job.primary_spoc?.id);
                                    return (
                                        <div
                                            key={s.id}
                                            data-testid={`job-office-spoc-${s.id}`}
                                            className={`p-3 border ${
                                                isPicked
                                                    ? "border-cobalt bg-[#EFF6FF]"
                                                    : "border-[#E5E5E5] bg-white"
                                            }`}
                                        >
                                            <div className="flex items-start gap-2">
                                                <User size={14} weight="fill" className="text-[#525252] mt-0.5 shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-bold text-ink truncate">
                                                        {s.name}
                                                    </p>
                                                    <p className="text-[10px] text-[#A3A3A3] mt-0.5">
                                                        {s.designation}
                                                    </p>
                                                    <div className="flex flex-col gap-0.5 mt-1.5 text-[11px] text-[#525252]">
                                                        <a
                                                            href={`mailto:${s.email}`}
                                                            className="inline-flex items-center gap-1 hover:text-cobalt truncate"
                                                            data-testid={`job-office-spoc-email-${s.id}`}
                                                        >
                                                            <EnvelopeSimple size={10} weight="bold" className="shrink-0" />
                                                            <span className="truncate">{s.email}</span>
                                                        </a>
                                                        <a
                                                            href={`tel:${(s.phone || "").replace(/[^0-9+]/g, "")}`}
                                                            className="inline-flex items-center gap-1 hover:text-cobalt"
                                                            data-testid={`job-office-spoc-phone-${s.id}`}
                                                        >
                                                            <PhoneIcon size={10} weight="bold" className="shrink-0" />
                                                            {s.phone}
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap gap-1 mt-2">
                                                {s.is_primary && (
                                                    <span className="inline-flex items-center gap-0.5 text-[9px] font-black uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-100 px-1.5 py-0.5">
                                                        <Star size={9} weight="fill" /> Primary
                                                    </span>
                                                )}
                                                {isPicked && (
                                                    <span className="text-[9px] font-black uppercase tracking-wider text-cobalt bg-white border border-cobalt/40 px-1.5 py-0.5">
                                                        Assigned to this JD
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {job.primary_spoc_is_fallback && (job.primary_location?.spocs || []).length > 0 && (
                            <p className="mt-2 text-[10px] text-[#A3A3A3] italic">
                                Showing the office&apos;s primary SPOC by default. Pick a specific
                                one via <span className="font-bold text-[#525252]">Edit</span> if the primary contact for this JD differs.
                            </p>
                        )}
                    </div>
                        </div>
                    )}
                </div>
            )}

            {/* Extra SPOCs — one-off contacts specific to this requirement.
                Primary contact comes from the Client Office (V1.5.5). */}
            {(job.spocs || []).length > 0 && (
                <div
                    data-testid="job-spocs-card"
                    className="mb-8 bg-white border border-[#E5E5E5] p-4 sm:p-5"
                >
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-3 inline-flex items-center gap-1.5">
                        <UsersIcon size={11} weight="bold" /> Extra SPOCs
                    </p>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {(job.spocs || []).map((s, i) => (
                            <div
                                key={i}
                                data-testid={`job-spoc-${i}`}
                                className="bg-[#FAFAFA] border border-[#E5E5E5] p-3"
                            >
                                <p className="font-bold text-ink text-sm">{s.name}</p>
                                {s.email && (
                                    <a
                                        href={`mailto:${s.email}`}
                                        className="mt-1 flex items-center gap-1.5 text-xs text-[#525252] hover:text-cobalt truncate"
                                    >
                                        <EnvelopeSimple size={11} weight="bold" />
                                        <span className="truncate">{s.email}</span>
                                    </a>
                                )}
                                {s.phone && (
                                    <a
                                        href={`tel:${s.phone.replace(/[^0-9+]/g, "")}`}
                                        className="mt-0.5 flex items-center gap-1.5 text-xs text-[#525252] hover:text-cobalt"
                                    >
                                        <PhoneIcon size={11} weight="bold" />
                                        <span>{s.phone}</span>
                                    </a>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Job description + requirements — collapsible so recruiters
                can read the full brief before adding a candidate, without
                pushing the Kanban board too far down the page. */}
            {(job.description || job.requirements) && (
                <div
                    data-testid="job-jd-panel"
                    className="mb-6 bg-white border border-[#E5E5E5]"
                >
                    <button
                        type="button"
                        data-testid="job-jd-toggle"
                        onClick={() => setJdExpanded((v) => !v)}
                        className="w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-3 hover:bg-[#FAFAFA] transition-colors"
                        aria-expanded={jdExpanded}
                    >
                        <div className="flex items-center gap-2 min-w-0">
                            <div className="w-7 h-7 bg-ink text-white flex items-center justify-center shrink-0">
                                <FileText size={13} weight="bold" />
                            </div>
                            <div className="text-left min-w-0">
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3]">
                                    Job description
                                </p>
                                <p className="font-bold text-ink text-sm truncate">
                                    {jdExpanded ? "Hide JD" : "Read the full JD"}
                                </p>
                            </div>
                        </div>
                        {jdExpanded ? (
                            <CaretUp size={16} weight="bold" className="text-[#525252] shrink-0" />
                        ) : (
                            <CaretDown size={16} weight="bold" className="text-[#525252] shrink-0" />
                        )}
                    </button>
                    {jdExpanded && (
                        <div
                            data-testid="job-jd-body"
                            className="border-t border-[#E5E5E5] px-4 sm:px-6 py-5 space-y-6"
                        >
                            {job.description && (
                                <div>
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-2">
                                        About the role
                                    </p>
                                    <div
                                        data-testid="job-description-body"
                                        className="whitespace-pre-wrap text-sm text-[#262626] leading-relaxed"
                                    >
                                        {job.description}
                                    </div>
                                </div>
                            )}
                            {job.requirements && (
                                <div>
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-2">
                                        Key requirements
                                    </p>
                                    <div
                                        data-testid="job-requirements-body"
                                        className="whitespace-pre-wrap text-sm text-[#262626] leading-relaxed"
                                    >
                                        {job.requirements}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Boost signal — highlights how many boost-plan candidates are in this pipeline */}
            {boostedCount > 0 && (
                <div
                    data-testid="job-boosted-summary"
                    className="mb-6 flex items-center gap-3 bg-gradient-to-r from-[#FFFBEB] via-white to-white border-l-4 border-[#F59E0B] px-4 py-3"
                >
                    <div className="w-8 h-8 bg-[#F59E0B] text-white flex items-center justify-center shrink-0">
                        <Crown size={16} weight="fill" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="font-bold text-ink text-sm">
                            {boostedCount} boosted candidate{boostedCount === 1 ? "" : "s"} in this pipeline
                        </p>
                        <p className="text-xs text-[#525252]">
                            Paid-plan holders. Cards are pinned to the top of each column for quick access.
                        </p>
                    </div>
                </div>
            )}

            {/* Kanban */}
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3]">
                    Pipeline
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                    {/* V1.4 — Awaiting-reg filter chip */}
                    <button
                        type="button"
                        onClick={() => setAwaitingRegOnly((s) => !s)}
                        data-testid="kanban-awaiting-reg-filter"
                        title={`Show only candidates who registered more than ${awaitingRegThreshold} day${awaitingRegThreshold === 1 ? "" : "s"} ago but never signed up.`}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider border transition-colors ${
                            awaitingRegOnly
                                ? "bg-amber-500 text-white border-amber-500"
                                : "bg-white text-[#525252] border-[#E5E5E5] hover:border-amber-500 hover:text-amber-700"
                        }`}
                    >
                        <span aria-hidden>⏳</span>
                        Awaiting reg {awaitingRegThreshold}d+
                        {awaitingRegCount > 0 && (
                            <span
                                data-testid="kanban-awaiting-reg-count"
                                className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] ${
                                    awaitingRegOnly
                                        ? "bg-white text-amber-700"
                                        : "bg-amber-500 text-white"
                                }`}
                            >
                                {awaitingRegCount}
                            </span>
                        )}
                    </button>
                    <div
                        className="inline-flex items-stretch border border-[#E5E5E5] bg-white p-0.5 text-[10px] font-black uppercase tracking-wider ml-2"
                        data-testid="kanban-density-toggle"
                        title="Toggle card density — Compact hides secondary badges so the board scans faster"
                    >
                        <span className="px-2 flex items-center text-[#A3A3A3] border-r border-[#F0F0F0] select-none">Density</span>
                        <button
                            type="button"
                            data-testid="kanban-density-compact"
                            onClick={() => setKanbanDensity("compact")}
                            className={`px-2 py-1 ${kanbanDensity === "compact" ? "bg-ink text-white" : "text-ink hover:bg-neutral-100"}`}
                        >Compact</button>
                        <button
                            type="button"
                            data-testid="kanban-density-cozy"
                            onClick={() => setKanbanDensity("cozy")}
                            className={`px-2 py-1 ${kanbanDensity === "cozy" ? "bg-ink text-white" : "text-ink hover:bg-neutral-100"}`}
                        >Cozy</button>
                        {kanbanDensity === "compact" && (
                            <button
                                type="button"
                                data-testid="kanban-compact-configure"
                                title="Reorder which 3 badges surface on compact cards"
                                onClick={() => setCompactSettingsOpen(true)}
                                className="px-2 py-1 border-l border-[#F0F0F0] text-cobalt hover:bg-cobalt/5"
                            >Configure</button>
                        )}
                    </div>
                    <div
                        className="inline-flex items-stretch border border-[#E5E5E5] bg-white p-0.5 text-[10px] font-black uppercase tracking-wider"
                        data-testid="kanban-sort-toggle"
                        title="Choose how each Kanban column is ranked"
                    >
                    {/* V1.5.8.17 — prefix label so recruiters instantly understand
                        clicking these re-orders the columns instead of opening a
                        panel. The buttons themselves stay short. */}
                    <span
                        className="px-2 flex items-center text-[#A3A3A3] border-r border-[#F0F0F0] select-none"
                        aria-hidden="true"
                    >
                        Rank by
                    </span>
                    <button
                        type="button"
                        data-testid="sort-mode-match"
                        onClick={() => {
                            if (sortMode !== "match") {
                                setSortMode("match");
                                toast.success("Ranked by AI match — highest match score first.");
                            }
                        }}
                        title="Sort each column by AI match score (highest first)"
                        className={`px-3 py-1.5 transition-colors ${sortMode === "match" ? "bg-ink text-white" : "text-[#525252] hover:text-ink"}`}
                    >
                        AI match
                    </button>
                    <button
                        type="button"
                        data-testid="sort-mode-conviction"
                        onClick={() => {
                            if (sortMode !== "conviction") {
                                setSortMode("conviction");
                                toast.success("Ranked by Conviction — 60% AI match + 40% reference rating.");
                            }
                        }}
                        title="Composite score: 60% AI match + 40% reference rating × 20"
                        className={`px-3 py-1.5 transition-colors ${sortMode === "conviction" ? "bg-ink text-white" : "text-[#525252] hover:text-ink"}`}
                    >
                        Conviction ✨
                    </button>
                    {/* V2.0.52 — Recent sort. Most-recently added/updated
                        candidates first. Default for new users so "who
                        applied today" is always visible on top. */}
                    <button
                        type="button"
                        data-testid="sort-mode-recent"
                        onClick={() => {
                            if (sortMode !== "recent") {
                                setSortMode("recent");
                                toast.success("Ranked by Recent — newest first.");
                            }
                        }}
                        title="Sort each column by most recently added or updated"
                        className={`px-3 py-1.5 transition-colors ${sortMode === "recent" ? "bg-ink text-white" : "text-[#525252] hover:text-ink"}`}
                    >
                        Recent
                    </button>
                    </div>
                    {/* V2.0.52 — Direct Applies filter. Toggles column
                        contents to ONLY show candidates who came in via
                        the public /apply/{id} form (source=public OR
                        origin=organic). Recruiters use it to triage
                        inbound public traffic without pipeline noise. */}
                    <button
                        type="button"
                        data-testid="kanban-direct-only-filter"
                        onClick={() => {
                            const next = !directOnly;
                            setDirectOnly(next);
                            toast.success(next
                                ? "Showing direct applies only."
                                : "Showing all candidates.");
                        }}
                        title="Show only candidates who applied directly via the public link"
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 border text-[10px] font-black uppercase tracking-wider transition-colors ${
                            directOnly
                                ? "bg-emerald-600 text-white border-emerald-600"
                                : "bg-white text-[#525252] border-[#E5E5E5] hover:border-emerald-600 hover:text-emerald-700"
                        }`}
                    >
                        <span aria-hidden>🎯</span>
                        Direct applies
                        {directApplyCount > 0 && (
                            <span
                                data-testid="kanban-direct-only-count"
                                className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] ${
                                    directOnly
                                        ? "bg-white text-emerald-700"
                                        : "bg-emerald-600 text-white"
                                }`}
                            >
                                {directApplyCount}
                            </span>
                        )}
                    </button>
                </div>
            </div>
            {/* V1.5.8.17 — always-visible caption so recruiters know at a glance
                which ranking is applied without hovering the toggle. */}
            <p
                data-testid="kanban-sort-caption"
                className="text-[10px] uppercase tracking-[0.18em] font-black text-[#A3A3A3] mb-2"
            >
                Columns ranked by{" "}
                <span className="text-cobalt">
                    {sortMode === "conviction"
                        ? "Conviction ✨ (60% AI match + 40% reference rating)"
                        : sortMode === "recent"
                            ? "Recent — most recently added or updated first"
                            : "AI match score"}
                </span>
                {" · Boosted candidates always pin to the top of each column."}
            </p>
            <div
                className={`flex items-start gap-4 overflow-x-auto kanban-scroll pb-4 kanban-density-${kanbanDensity}`}
                data-testid="kanban-board"
                data-density={kanbanDensity}
            >
                {STAGES.map((s) => (
                    <div
                        key={s}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => onDrop(s)}
                        data-testid={`kanban-column-${s.toLowerCase()}`}
                        className="flex-shrink-0 w-[260px] sm:w-[300px] flex flex-col gap-3 bg-[#F5F5F5] border border-[#E5E5E5] p-3 min-h-[400px]"
                    >
                        <div className="flex items-center justify-between px-1">
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-bold uppercase tracking-[0.15em] text-ink">
                                    {s}
                                </span>
                                {s === "Interview" && (
                                    <span
                                        data-testid="interview-legend-chip"
                                        title="After the interview, click a card → 'Record interview outcome'. Chips shown: Selected (passed) · ↻ Rescheduled (with count) · Rejected (moves to Rejected column)."
                                        className="inline-flex items-center justify-center w-4 h-4 border border-[#A3A3A3] text-[#525252] text-[10px] font-bold cursor-help hover:bg-white transition-colors"
                                    >
                                        i
                                    </span>
                                )}
                            </div>
                            <span className="text-xs font-bold text-[#A3A3A3]">
                                {grouped[s].length}
                            </span>
                        </div>
                        {grouped[s].map((c) => (
                            <div
                                key={c.id}
                                draggable={canWrite}
                                onDragStart={canWrite ? () => setDragId(c.id) : undefined}
                                onClick={() => setSelectedId(c.id)}
                                data-testid={`candidate-card-${c.id}`}
                                className={`p-4 border transition-colors flex flex-col gap-2 ${
                                    canWrite ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"
                                } ${
                                    c.is_boosted
                                        ? "bg-gradient-to-br from-[#FFFBEB] via-white to-white border-[#F59E0B] hover:border-[#B45309]"
                                        : "bg-white border-[#E5E5E5] hover:border-ink"
                                }`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1 flex items-start gap-2">
                                        {/* V1.5.3 (a3) — kanban card avatar */}
                                        <KanbanAvatar url={c.avatar_url} name={c.name} />
                                        <div className="min-w-0 flex-1">
                                        <p className="font-bold text-ink text-sm flex items-center gap-1.5 flex-wrap">
                                            <span className="truncate">{c.name}</span>
                                            <VerifiedTierChip tier={c.verification_tier} size="xs" />
                                            {c.is_boosted && (
                                                <span
                                                    data-testid={`kanban-boosted-badge-${c.id}`}
                                                    title="Boosted candidate — active paid plan"
                                                    className="inline-flex items-center gap-0.5 bg-[#F59E0B] text-white text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5"
                                                >
                                                    <Crown size={8} weight="fill" /> Boosted
                                                </span>
                                            )}
                                            {!c.candidate_user_id && c.drip_state?.status === "active" && (
                                                <span
                                                    data-testid={`kanban-awaiting-badge-${c.id}`}
                                                    title={`Awaiting portal registration — reminders sent ${c.drip_state.sent_count || 0}/5`}
                                                    className="inline-flex items-center gap-0.5 bg-amber-100 text-amber-900 text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5"
                                                >
                                                    Awaiting reg
                                                </span>
                                            )}
                                            {c.offer_outcome?.status === "accepted" && (
                                                <span
                                                    data-testid={`kanban-outcome-badge-${c.id}`}
                                                    title="Offer accepted by candidate"
                                                    className="inline-flex items-center gap-0.5 bg-emerald-100 text-emerald-800 text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5"
                                                >
                                                    Offer accepted
                                                </span>
                                            )}
                                            {c.offer_outcome?.status === "rejected" && (
                                                <span
                                                    data-testid={`kanban-outcome-badge-${c.id}`}
                                                    title="Offer declined by candidate"
                                                    className="inline-flex items-center gap-0.5 bg-red-100 text-red-800 text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5"
                                                >
                                                    Offer declined
                                                </span>
                                            )}
                                            {c.hire_outcome?.status === "hired" && (
                                                <span
                                                    data-testid={`kanban-outcome-badge-${c.id}`}
                                                    title="Candidate joined"
                                                    className="inline-flex items-center gap-0.5 bg-emerald-100 text-emerald-800 text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5"
                                                >
                                                    Joined
                                                </span>
                                            )}
                                            {c.hire_outcome?.status === "no_show" && (
                                                <span
                                                    data-testid={`kanban-outcome-badge-${c.id}`}
                                                    title="Candidate did not join"
                                                    className="inline-flex items-center gap-0.5 bg-red-100 text-red-800 text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5"
                                                >
                                                    No-show
                                                </span>
                                            )}
                                            {c.interview_outcome?.status === "selected" && (
                                                <span
                                                    data-testid={`kanban-interview-badge-${c.id}`}
                                                    title={c.interview_outcome.feedback || "Cleared interview"}
                                                    className="inline-flex items-center gap-0.5 bg-emerald-100 text-emerald-800 text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5"
                                                >
                                                    Selected
                                                </span>
                                            )}
                                            {c.interview_outcome?.status === "rescheduled" && (
                                                <span
                                                    data-testid={`kanban-interview-badge-${c.id}`}
                                                    title={`Reschedule #${c.interview_outcome.count || 1}${c.interview_outcome.reason ? " — " + c.interview_outcome.reason : ""}`}
                                                    className="inline-flex items-center gap-0.5 bg-amber-100 text-amber-800 text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5"
                                                >
                                                    ↻ Rescheduled{(c.interview_outcome.count || 1) > 1 ? ` ×${c.interview_outcome.count}` : ""}
                                                </span>
                                            )}
                                            {c.stage === "Offer" && !c.offer_outcome && (
                                                <span
                                                    data-testid={`kanban-outcome-pending-${c.id}`}
                                                    title="Record the offer outcome"
                                                    className="inline-flex items-center gap-0.5 bg-[#FEF3C7] text-[#92400E] text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5"
                                                >
                                                    Outcome pending
                                                </span>
                                            )}
                                            {c.stage === "Hired" && !c.hire_outcome && (
                                                <span
                                                    data-testid={`kanban-outcome-pending-${c.id}`}
                                                    title="Confirm the joining"
                                                    className="inline-flex items-center gap-0.5 bg-[#FEF3C7] text-[#92400E] text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5"
                                                >
                                                    Confirm joining
                                                </span>
                                            )}
                                        </p>
                                        {c.code && (
                                            <p
                                                data-testid={`kanban-candidate-code-${c.id}`}
                                                className="font-mono text-[9px] tracking-wider text-[#A3A3A3] mt-0.5"
                                            >
                                                {c.code}
                                            </p>
                                        )}
                                        {/* V2.0.52 — origin chip so recruiters
                                            can instantly tell how the candidate
                                            landed on this card (public apply,
                                            DaddyTalk chat, referral, recruiter
                                            upload). Green "Self-signup" == a
                                            direct applier — the group the new
                                            "Direct applies" filter targets. */}
                                        {/* V2.1.1 iter169 — Origin chip only rendered
                                            in cozy mode. Compact mode surfaces its
                                            equivalent through <KanbanCompactBadges>
                                            below (top-3 priority selector). */}
                                        {kanbanDensity !== "compact" && (
                                        _isDirectApply(c) ? (
                                            <span
                                                data-testid={`kanban-origin-chip-${c.id}`}
                                                title="Applied directly via the public /apply link"
                                                className="inline-flex items-center gap-0.5 mt-1 text-[8px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5"
                                            >
                                                Self-signup
                                            </span>
                                        ) : (c.origin === "daddytalk_fast_track" || (c.source || "").toLowerCase() === "fast_track") ? (
                                            <span
                                                data-testid={`kanban-origin-chip-${c.id}`}
                                                title="Converted from a DaddyTalk chat via Fast-Track"
                                                className="inline-flex items-center gap-0.5 mt-1 text-[8px] font-black uppercase tracking-wider bg-cobalt/10 text-cobalt px-1.5 py-0.5"
                                            >
                                                Fast-Track
                                            </span>
                                        ) : (c.source || "").toLowerCase() === "referral" ? (
                                            <span
                                                data-testid={`kanban-origin-chip-${c.id}`}
                                                title="Came via a candidate referral link"
                                                className="inline-flex items-center gap-0.5 mt-1 text-[8px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5"
                                            >
                                                Referral
                                            </span>
                                        ) : (
                                            <span
                                                data-testid={`kanban-origin-chip-${c.id}`}
                                                title="Recruiter-added"
                                                className="inline-flex items-center gap-0.5 mt-1 text-[8px] font-black uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.5"
                                            >
                                                Sourced
                                            </span>
                                        ))}
                                    </div>
                                    </div>{/* /kanban-avatar+name wrapper (V1.5.3) */}
                                    <div className="flex items-center gap-1.5 flex-wrap justify-end">
                                        <ScoreBadge score={c.match_score} />
                                        <ReferenceScoreChip
                                            summary={c.reference_summary}
                                            testid={`ref-score-chip-${c.id}`}
                                        />
                                        {sortMode === "conviction" && c.reference_summary?.avg_rating && (
                                            <ConvictionBadge
                                                score={convictionOf(c)}
                                                testid={`conviction-badge-${c.id}`}
                                            />
                                        )}
                                    </div>
                                </div>
                                {/* V2.1.1 iter169 — compact mode renders a proper
                                    3-badge React block instead of hiding chips
                                    via CSS. See <KanbanCompactBadges> above. */}
                                {kanbanDensity === "compact" ? (
                                    <KanbanCompactBadges c={c} isDirectApply={_isDirectApply(c)} order={compactOrder} />
                                ) : (
                                <>
                                <p className="text-xs text-[#525252] truncate">
                                    {c.current_title || "—"}
                                </p>
                                <SourceBadge source={c.source} />
                                <CvFreshnessBadge candidate={c} className="ml-1" />
                                {/* V1.6.0 M6 — quick-jump pill to the DaddyTalk thread
                                    that spawned this candidate card. Only appears when
                                    the candidate row has a `daddytalk_thread_id`. */}
                                {c.daddytalk_thread_id && (
                                    <a
                                        href={`/messages?thread=${c.daddytalk_thread_id}`}
                                        onClick={(e) => e.stopPropagation()}
                                        data-testid={`kanban-daddytalk-pill-${c.id}`}
                                        title="Jump back to the DaddyTalk thread"
                                        className="inline-flex items-center gap-1 border border-cobalt/40 bg-cobalt/5 text-cobalt text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 hover:bg-cobalt hover:text-white transition-colors ml-1"
                                    >
                                        <ChatCircleDots size={10} weight="bold" />
                                        Open chat
                                    </a>
                                )}
                                {/* V1.4 badges — Awaiting reg X days + Email delayed 5m+ */}
                                {(c.is_awaiting_reg || c.email_delayed) && (
                                    <div className="flex flex-wrap gap-1.5" data-testid={`kanban-badges-${c.id}`}>
                                        {c.is_awaiting_reg && (
                                            <span
                                                data-testid={`kanban-awaiting-reg-badge-${c.id}`}
                                                title={`Added ${c.awaiting_reg_days} day${c.awaiting_reg_days === 1 ? "" : "s"} ago — never registered. Copy their sign-in link and nudge over WhatsApp.`}
                                                className="inline-flex items-center gap-1 border border-amber-400 bg-amber-50 text-amber-900 text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5"
                                            >
                                                <Clock size={10} weight="bold" />
                                                Awaiting reg · {c.awaiting_reg_days}d
                                            </span>
                                        )}
                                        {c.email_delayed && (
                                            <span
                                                data-testid={`kanban-email-delayed-badge-${c.id}`}
                                                title="Gmail/Yahoo is throttling delivery for this candidate — use Copy link on the drawer to share via WhatsApp instead."
                                                className="inline-flex items-center gap-1 border border-orange-500 bg-orange-50 text-orange-900 text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5"
                                            >
                                                <Warning size={10} weight="bold" />
                                                Email delayed 5m+
                                            </span>
                                        )}
                                    </div>
                                )}
                                {!c.candidate_user_id && c.drip_state?.status === "active" && canWrite && (
                                    <button
                                        type="button"
                                        data-testid={`kanban-nudge-${c.id}`}
                                        onClick={(e) => nudgeCandidate(c.id, e)}
                                        disabled={nudgingId === c.id}
                                        title="Send a registration reminder email now"
                                        className="self-start inline-flex items-center gap-1 border border-amber-300 hover:border-amber-500 hover:bg-amber-50 text-amber-900 px-2 py-1 text-[10px] font-black uppercase tracking-wider transition-colors disabled:opacity-60"
                                    >
                                        <Bell size={10} weight="bold" />
                                        {nudgingId === c.id ? "Sending…" : "Nudge now"}
                                    </button>
                                )}
                                {c.skills?.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                        {c.skills.slice(0, 3).map((sk, i) => (
                                            <span
                                                key={`${sk}-${i}`}
                                                className="text-[10px] font-bold text-[#525252] border border-[#E5E5E5] px-1.5 py-0.5"
                                            >
                                                {sk}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <AddedByChip
                                    name={c.added_by_name}
                                    isTeamPool={!!c.is_team_pool}
                                    isMine={!!c.owner_id && c.owner_id === me?.id}
                                    size="sm"
                                    className="mt-1"
                                />
                                </>
                                )}
                            </div>
                        ))}
                        {grouped[s].length === 0 && (
                            <p className="text-xs text-[#A3A3A3] px-1">Drop candidates here</p>
                        )}
                    </div>
                ))}
            </div>

            {/* V2.1.1 iter170 — Cold Kanban Rescue drawer */}
            <RescuePanel
                jobId={id}
                jobTitle={job?.title}
                open={rescueOpen}
                onClose={() => setRescueOpen(false)}
                onRescued={() => { load(); }}
            />

            {/* V2.1.1 iter170 — Compact-mode badge priority reorder */}
            <KanbanCompactSettings
                open={compactSettingsOpen}
                onClose={() => setCompactSettingsOpen(false)}
                order={compactOrder}
                labels={COMPACT_BADGE_LABELS}
                onSave={(next) => { setCompactOrder(next); saveCompactOrder(next); }}
                onReset={() => { setCompactOrder(DEFAULT_COMPACT_ORDER); saveCompactOrder(DEFAULT_COMPACT_ORDER); }}
            />

            {/* New candidate modal */}
            {showForm && (
                <div
                    className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-40 flex items-center justify-center p-4"
                    onClick={() => setShowForm(false)}
                >
                    <div
                        data-testid="new-candidate-form"
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white border border-[#E5E5E5] w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                    >
                        <div className="flex items-center justify-between p-6 border-b border-[#E5E5E5] sticky top-0 bg-white">
                            <h2 className="font-heading text-2xl font-bold tracking-tight">
                                Add candidate
                            </h2>
                            <button onClick={() => setShowForm(false)} className="p-2 hover:bg-[#F5F5F5]">
                                <X size={18} weight="bold" />
                            </button>
                        </div>
                        <form onSubmit={submit} className="p-6 space-y-4">
                            {/* Auto-fill upload prompt — clicks the hidden file input via the label */}
                            <label
                                data-testid="autofill-banner"
                                className="cursor-pointer flex items-center gap-3 bg-gradient-to-r from-[#EFF6FF] to-[#EEF2FF] border border-[#C7D2FE] px-4 py-3 hover:from-[#DBEAFE] hover:to-[#E0E7FF] transition-colors"
                            >
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-bold text-[#3730A3] leading-tight">
                                        Upload CV to auto-fill this form
                                    </p>
                                    <p className="text-xs text-[#4338CA] mt-0.5">
                                        We extract name, email, phone, title, location, LinkedIn, skills &amp; experience — you can still edit anything.
                                    </p>
                                </div>
                                <span className="inline-flex items-center gap-1 bg-white border border-[#C7D2FE] px-3 py-1.5 text-xs font-bold text-[#3730A3] uppercase tracking-wider shrink-0">
                                    <UploadSimple size={12} weight="bold" />
                                    {pdfLoading ? "Parsing…" : "Upload"}
                                </span>
                                <input
                                    data-testid="autofill-banner-input"
                                    type="file"
                                    accept=".pdf,.docx,.jpg,.jpeg,.png,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png"
                                    className="hidden"
                                    onChange={handlePdf}
                                    disabled={pdfLoading}
                                />
                            </label>
                            <DuplicateCheckBanner
                                email={form.email}
                                phone={form.phone}
                                jobId={id}
                                onStatus={setDupStatus}
                                onOpenCandidate={(cid) => setSelectedId(cid)}
                            />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {[
                                    ["name", "Full name", true],
                                    ["email", "Email", true],
                                    ["phone", "Phone", false],
                                    ["current_title", "Current title", false],
                                    ["years_experience", "Years experience", false],
                                ].map(([k, label, req]) => (
                                    <div key={k}>
                                        <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                            {label}
                                        </label>
                                        <input
                                            data-testid={`cand-${k}-input`}
                                            type={k === "years_experience" ? "number" : k === "email" ? "email" : k === "phone" ? "tel" : "text"}
                                            value={form[k]}
                                            required={req}
                                            placeholder={k === "phone" ? "+91 9876543210" : undefined}
                                            pattern={k === "phone" ? "^\\+?[0-9\\s\\-().]{10,25}$" : undefined}
                                            minLength={k === "name" ? 2 : undefined}
                                            title={k === "phone" ? "Enter 10–15 digits; '+' and spaces/dashes are allowed" : undefined}
                                            onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                                            className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 text-base focus:outline-none focus:border-cobalt"
                                        />
                                        {k === "phone" && (
                                            <p className="mt-1 text-[10px] text-[#A3A3A3]">
                                                10–15 digits. Optional leading &apos;+&apos;.
                                            </p>
                                        )}
                                    </div>
                                ))}
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                        Location
                                    </label>
                                    <AutocompleteInput
                                        testId="cand-location-input"
                                        field="location"
                                        staticOptions={LOCATION_SUGGESTIONS}
                                        value={form.location}
                                        onChange={(v) => setForm({ ...form, location: v })}
                                        placeholder="e.g. Bangalore, Remote"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                    Skills
                                </label>
                                <SkillsChipInput
                                    testId="cand-skills"
                                    value={candSkills}
                                    onChange={setCandSkills}
                                    placeholder="React, TypeScript, Node.js…"
                                    staticOptions={job?.skills_required || []}
                                />
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                    LinkedIn URL
                                </label>
                                <input
                                    type="url"
                                    value={form.linkedin}
                                    onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
                                    className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 text-base focus:outline-none focus:border-cobalt"
                                />
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">
                                        Resume text (paste)
                                    </label>
                                    <label
                                        data-testid="cand-upload-pdf-label"
                                        className="text-xs font-bold uppercase tracking-wider text-cobalt cursor-pointer inline-flex items-center gap-1 hover:underline"
                                    >
                                        <UploadSimple size={12} weight="bold" />
                                        {pdfLoading ? "Parsing…" : "Upload resume"}
                                        <input
                                            data-testid="cand-pdf-input"
                                            type="file"
                                            accept=".pdf,.docx,.jpg,.jpeg,.png,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png"
                                            className="hidden"
                                            onChange={handlePdf}
                                            disabled={pdfLoading}
                                        />
                                    </label>
                                </div>
                                <textarea
                                    data-testid="cand-resume-input"
                                    value={typeof form.resume_text === "string" ? form.resume_text : ""}
                                    required
                                    minLength={30}
                                    rows={8}
                                    placeholder="Paste the candidate's resume here (min 30 characters)"
                                    onChange={(e) => setForm({ ...form, resume_text: e.target.value })}
                                    onPaste={smartPasteHandler((v) => setForm((f) => ({ ...f, resume_text: v })))}
                                    className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 text-base focus:outline-none focus:border-cobalt whitespace-pre-wrap"
                                />
                                <p className="mt-1 text-[10px] text-[#A3A3A3]">
                                    {form.resume_text.trim().length}/30 characters minimum · Upload PDF, DOCX, JPG or PNG — we auto-extract text (OCR for scans).
                                </p>
                            </div>
                            {err && (
                                <div className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-sm px-4 py-3">
                                    {err}
                                </div>
                            )}
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setShowForm(false)}
                                    className="border border-[#E5E5E5] hover:border-ink px-6 py-3 font-bold tracking-wide text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    data-testid="cand-submit-button"
                                    disabled={saving || dupStatus === "block"}
                                    className="flex-1 bg-cobalt hover:bg-cobalt-dark disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-3 font-bold tracking-wide text-sm"
                                >
                                    {saving
                                        ? "Saving…"
                                        : dupStatus === "block"
                                            ? "Already on this requirement"
                                            : "Add candidate"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {selectedId && (
                <CandidateDrawer
                    candidateId={selectedId}
                    onClose={() => setSelectedId(null)}
                    onUpdated={load}
                    onOpenCandidate={(other) => setSelectedId(other)}
                />
            )}

            {shareOpen && (
                <ShareJobModal
                    open={shareOpen}
                    onClose={() => setShareOpen(false)}
                    jobId={id}
                    jobTitle={job?.title || "Open role"}
                    clientName={job?.client?.name}
                />
            )}

            {editOpen && job && (
                <EditJobModal
                    open={editOpen}
                    job={job}
                    onClose={() => setEditOpen(false)}
                    onSaved={(updated) => setJob((prev) => ({ ...prev, ...updated }))}
                />
            )}

            {/* V1.5.5.13 — Inline SPOC creation from JD empty-state */}
            {showAddSpocModal && job?.client_id && job?.primary_location && (
                <SpocInlineModal
                    open={showAddSpocModal}
                    clientId={job.client_id}
                    locationId={job.primary_location.id}
                    locationLabel={job.primary_location.label}
                    onClose={() => setShowAddSpocModal(false)}
                    onCreated={() => load()}
                />
            )}
        </div>
    );
}
